/**
 * CORS Client-Side Interceptor
 * Manages CORS-related headers and cross-origin requests
 */

import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import { mayRetryRequest } from './http-retry-policy';

@Injectable()
export class CorsInterceptor implements HttpInterceptor {

  private readonly allowedDomains = [
    'befret.be',
    'dev.befret.be',
    'test.befret.be',
    'befret-development.firebaseapp.com',
    'befret-acceptance.firebaseapp.com',
    'befret-acceptance.web.app',
    'befret-production.firebaseapp.com',
    'us-central1-befret-development.cloudfunctions.net',
    'us-central1-befret-acceptance.cloudfunctions.net',
    'us-central1-befret-production.cloudfunctions.net',
    'europe-west1-befret-development.cloudfunctions.net',
    'europe-west1-befret-acceptance.cloudfunctions.net',
    'europe-west1-befret-production.cloudfunctions.net',
    'api.stripe.com',
    'js.stripe.com',
    'api.sendgrid.com',
    'api.twilio.com',
    // HERE Maps API
    'geocode.search.hereapi.com',
    'autosuggest.search.hereapi.com',
    'discover.search.hereapi.com',
    'lookup.search.hereapi.com',
    // APIs de géolocalisation
    'ipapi.co',
    'get.geojs.io',
    'api.bigdatacloud.net',
    'ipinfo.io',
    'api.ipgeolocation.io',
    'localhost', // Allow localhost for development
    '127.0.0.1' // Allow local IP for development
  ];

  private readonly requestTimeout = 30000; // 30 seconds
  private readonly maxRetries = 2;

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if this is a cross-origin request
    const isCrossOrigin = this.isCrossOriginRequest(req.url);
    
    if (!isCrossOrigin) {
      // Same-origin request, proceed normally
      return next.handle(req).pipe(
        timeout(this.requestTimeout),
        catchError(this.handleError.bind(this))
      );
    }

    // Validate the target domain
    if (!this.isAllowedDomain(req.url)) {
      console.error('CORS: Request to unauthorized domain blocked:', req.url);
      return throwError(() => new Error('Request to unauthorized domain'));
    }

    // Clone request and add only essential headers
    const corsRequest = req.clone({
      setHeaders: {
        'Content-Type': 'application/json'
      }
    });

    // Remove problematic headers for CORS
    let cleanRequest = corsRequest;
    const problematicHeaders = ['X-Forwarded-For', 'X-Real-IP', 'X-Forwarded-Proto'];
    
    problematicHeaders.forEach(header => {
      if (cleanRequest.headers.has(header)) {
        cleanRequest = cleanRequest.clone({
          headers: cleanRequest.headers.delete(header)
        });
      }
    });

    return next.handle(cleanRequest).pipe(
      timeout(this.requestTimeout),
      retry({
        count: this.maxRetries,
        delay: (error, retryCount) => {
          // 🔴 #2156 — une panne passagère, ET une méthode idempotente : un POST
          // vers `createInvoice` dont la réponse s'est perdue a peut-être créé
          // une session Stripe ; le rejouer en crée une seconde.
          if (mayRetryRequest(cleanRequest, error)) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
            console.log(`CORS: Retrying request (${retryCount}/${this.maxRetries}) after ${delay}ms`);
            return new Promise(resolve => setTimeout(resolve, delay));
          }
          throw error;
        }
      }),
      catchError((error) => this.handleCorsError(error, req.url))
    );
  }

  /**
   * Check if the request is cross-origin
   */
  private isCrossOriginRequest(url: string): boolean {
    try {
      const requestUrl = new URL(url, window.location.origin);
      return requestUrl.origin !== window.location.origin;
    } catch {
      return false;
    }
  }

  /**
   * Check if the domain is in the allowed list
   */
  private isAllowedDomain(url: string): boolean {
    try {
      const requestUrl = new URL(url, window.location.origin);
      const hostname = requestUrl.hostname.toLowerCase();
      
      return this.allowedDomains.some(domain => 
        hostname === domain || hostname.endsWith('.' + domain)
      );
    } catch {
      return false;
    }
  }

  /**
   * Generate unique request ID for tracking
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Journalise l'erreur et la RENVOIE TELLE QUELLE.
   *
   * 🔴 #2156 — comme dans `SecurityInterceptor`, cette méthode remplaçait
   * `error.error` par `{ message }` : en déploiement, `createInvoice` est
   * appelée sur une autre origine (cloudfunctions.net), donc par cette branche,
   * et le code d'un refus métier (`TOTAL_BELOW_MINIMUM`…) y était effacé une
   * seconde fois. Corriger le seul `SecurityInterceptor` n'aurait rien changé.
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    console.error('HTTP Error:', {
      status: error.status,
      url: error.url || undefined,
      timestamp: new Date().toISOString()
    });
    return throwError(() => error);
  }

  /**
   * Handle CORS-specific errors
   */
  private handleCorsError(error: HttpErrorResponse, url: string): Observable<never> {
    if (error.status === 0 && error.error instanceof ProgressEvent) {
      // Likely a CORS error
      console.error('CORS Error:', {
        url,
        message: 'Cross-origin request failed',
        timestamp: new Date().toISOString(),
        possibleCauses: [
          'Server not configured for CORS',
          'Origin not in server allowlist',
          'Network connectivity issue',
          'Server unavailable'
        ]
      });

      const corsError = new HttpErrorResponse({
        error: { 
          message: 'Cross-origin request failed. Please try again or contact support if the problem persists.',
          type: 'CORS_ERROR'
        },
        status: 0,
        statusText: 'CORS Error',
        url
      });

      return throwError(() => corsError);
    }

    return this.handleError(error);
  }
}